import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { BadgeCheck, Info, Mail, Loader2, Eye, EyeOff } from "lucide-react";
import AuthSplitLayout from "../../components/auth/AuthSplitLayout";
import {
  verifyStart,
  verifySuccess,
  verifyFailure,
  resetAuthFlowState,
} from "../../store/slices/userSlice";
import { verifyConsumerOtp, getErrorMessage } from "../../api/userAuthApi";
import showToast  from "../../utils/toast";
import { clearApplyIntent, readApplyIntent } from "../../utils/applyIntent";
import { OTP_PROGRESS_BLOCK_REASON } from "../../utils/authFlowGuards";

const inputClassName =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#FFD221] focus:ring-4 focus:ring-[#FFD221]/20";

const VerifyAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pendingVerification, verifyStatus, verifyError, isAuthenticated, token } =
    useSelector((s) => s.infoboxx_consumer);

  const [verificationEmail, setVerificationEmail] = useState(
    pendingVerification?.email || ""
  );
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (pendingVerification?.email) {
      setVerificationEmail(pendingVerification.email);
    }
  }, [pendingVerification?.email]);

  const postAuthRedirect = location.state?.postAuthRedirect || readApplyIntent();
  if (isAuthenticated && token) {
    return <Navigate to={postAuthRedirect?.path || "/"} state={postAuthRedirect?.state} replace />;
  }

  if (!pendingVerification?.email && !isAuthenticated) {
    return <Navigate to="/signup" replace />;
  }

  const verifying = verifyStatus === "loading";

  const handleVerify = async (event) => {
    event.preventDefault();
    const email = verificationEmail.trim();
    const otp = password;
    if (!otp) {
      showToast.warning("Enter the code from your email.");
      return;
    }
    try {
      // Verification is handled without thunks: call API and dispatch plain actions.
      dispatch(verifyStart());
      const payload = await verifyConsumerOtp({ email, otp });
      dispatch(verifySuccess(payload));
      showToast.success("You are signed in.");
      const next = location.state?.postAuthRedirect || readApplyIntent();
      clearApplyIntent();
      navigate(next?.path || "/", { replace: true, state: next?.state });
    } catch (err) {
      const message = getErrorMessage(err);
      dispatch(verifyFailure(message));
      showToast.error(typeof message === "string" ? message : "Verification failed");
    }
  };

  return (
    <AuthSplitLayout
      title="Verify your account"
      subtitle="Enter the code we sent to your email. After this step you will be signed in."
      sideBadge="ACCOUNT VERIFICATION"
      sideTitle="One quick check, full Infoboxx access unlocked."
      sideDescription="Verification keeps your account secure and connects you to the right expert partners for your topics."
      sideBullets={[
        "Fast verification",
        "Secure account access",
        "Instant access to expert content",
      ]}
      footer={
        <p>
          Wrong email?{" "}
          <Link
            to="/signup"
            onClick={() => dispatch(resetAuthFlowState())}
            className="font-semibold text-black hover:text-[#E5B800]"
          >
            Start over
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={handleVerify}>
        {pendingVerification?.first_name ? (
          <p className="text-sm text-slate-600">
            Hi{" "}
            <span className="font-semibold text-slate-900">
              {pendingVerification.first_name}
            </span>
            , almost there.
          </p>
        ) : null}

        <label className="space-y-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Verification email</span>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={17} />
            <input
              className={`${inputClassName} pl-10`}
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={verificationEmail}
              onChange={(event) => setVerificationEmail(event.target.value)}
              disabled={verifying || !!pendingVerification?.email}
              required
            />
          </div>
          {pendingVerification?.email ? (
            <p className="text-[11px] text-slate-500">Using the email from your registration.</p>
          ) : null}
        </label>

        <div className="flex items-start gap-3 rounded-xl border border-[#FFD221]/30 bg-[#FFD221]/10 px-4 py-3">
          <Info size={16} className="mt-0.5 shrink-0 text-[#E5B800]" />
          <p className="text-xs leading-relaxed text-gray-700">{OTP_PROGRESS_BLOCK_REASON}</p>
        </div>

        <label className="space-y-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Verification Password
          </span>
          <div className="relative">
            <input
              className={`${inputClassName} pr-10`}
              type={showPassword ? "text" : "password"}
              autoComplete="off"
              placeholder="Enter the password from your email"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={verifying}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
            >
              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
          <p className="text-[11px] text-slate-500">
            Enter the password sent to your email address.
          </p>
        </label>

        <AnimatePresence>
          {verifyError ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {verifyError}
            </motion.p>
          ) : null}
        </AnimatePresence>

        <motion.button
          whileHover={{ scale: verifying ? 1 : 1.01 }}
          whileTap={{ scale: verifying ? 1 : 0.99 }}
          type="submit"
          disabled={verifying}
          className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-black text-black shadow-lg transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          style={{ background: "#FFD221" }}
        >
          {verifying ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Verifying…
            </>
          ) : (
            <span className="inline-flex items-center gap-2">
              <BadgeCheck size={17} />
              Verify & sign in
            </span>
          )}
        </motion.button>
      </form>
    </AuthSplitLayout>
  );
};

export default VerifyAccount;